import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Globe, MessageCircle, DollarSign, X, Rocket } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { SET_LAUNCHTOKENPLAYLIST, SET_LAUNCHTOKENSONG, SET_WALLETBTN } from '@/redux/features/music/musicSlice';
import axios from 'axios';
import { VersionedTransaction } from '@solana/web3.js';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { TwitterXIcon } from '../navbar/SocialIcons';
import frog from "@/assets/footer-frog.png"

function LaunchTokenDialog() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [coverPreview, setCoverPreview] = useState(null);
    const { walletBtn, launchTokenSong, launchTokenPlaylist } = useSelector((state) => state.music);
    const { connected, publicKey, sendTransaction } = useWallet()
    const { connection } = useConnection()
    const [tokenName, setTokenName] = useState('')
    const [ticker, setTicker] = useState('')
    const [description, setDescription] = useState('')
    const [website, setWebsite] = useState('')
    const [twitter, setTwitter] = useState('')
    const [telegram, setTelegram] = useState('')
    const [buyAmount, setBuyAmount] = useState(0.001)
    const [cover, setCover] = useState(null)
    const [songId, setSongId] = useState(null)
    const [xError, setXError] = useState('');
    const [telegramError, setTelegramError] = useState('');
    const [buyAmountError, setBuyAmountError] = useState('');
    const { user } = useSelector((state) => state.auth)
    const [loading, setLoading] = useState(false);
    const userHavePlan = user?.plan === 1 || user?.plan === 2 || user?.plan === 3

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setCover(file);
            const reader = new FileReader();
            reader.onload = (event) => {
                setCoverPreview(event.target?.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        const token = localStorage.getItem('token')
        // validations
        if (!connected) {
            toast.error('Please connect your wallet first');
            return;
        }
        if (!tokenName || !ticker) {
            toast.error('Please enter token details');
            return;
        }
        if (telegramError) {
            toast.error('Please enter a valid telegram url');
            return;
        }
        if (xError) {
            toast.error('Please enter a valid x url');
            return;
        }
        if (buyAmount < 0.001) {
            toast.error('Minimum buy amount is 0.001 SOL');
            return;
        }

        const newFormData = new FormData()
        // clear the form data for song or playlist
        newFormData.delete('song_id')
        newFormData.delete('playlist_id')
        // Check if we're launching a token for a song or playlist
        if (launchTokenSong) {
            newFormData.append('song_id', launchTokenSong?.song_id)
        } else if (launchTokenPlaylist) {
            newFormData.append('playlist_id', launchTokenPlaylist?.playlistId)
        }
        newFormData.append('wallet', publicKey?.toString())
        newFormData.append('name', tokenName)
        newFormData.append('ticker', ticker)
        newFormData.append('description', description)
        newFormData.append('website', website)
        newFormData.append('twitter', twitter)
        newFormData.append('telegram', telegram)
        newFormData.append('buy_amount', buyAmount)
        if (cover) newFormData.append('cover', cover)

        try {
            setLoading(true)
            toast.loading('Launching token...', { id: "token" })
            const { data } = await axios.post(`https://engine.soundofmeme.com/song/mint/`, newFormData, { headers: { Authorization: `Bearer ${token}` } })
            if (!data?.encoded_tx) {
                throw new Error('No transaction data received');
            }
            // eslint-disable-next-line no-undef
            const transaction = VersionedTransaction.deserialize(Buffer.from(data?.encoded_tx, 'base64'))
            const signedTransaction = await sendTransaction(transaction, connection)
            if (!signedTransaction) {
                throw new Error('Failed to sign transaction');
            }
            const confirmData = {
                signature: signedTransaction,
                token: data?.token,
                last_hash: data?.last_hash
            }

            // Add either song_id or playlist_id based on which type we're launching
            if (launchTokenSong) {
                confirmData.song_id = songId;
            } else if (launchTokenPlaylist) {
                confirmData.playlist_id = launchTokenPlaylist?.playlistId;
            }

            toast.loading('Confirming transaction...', { id: "token" })
            const confirmRes = await axios.post("https://engine.soundofmeme.com/song/confirm", confirmData, { headers: { Authorization: `Bearer ${token}` } })
            console.log(confirmRes?.data)
            toast.success('Token launched successfully', { id: "token" })
            // Open token page in new tab after successful launch
            if (confirmRes?.data?.token) {
                window.open(`https://pump.fun/coin/${confirmRes?.data?.token}`, '_blank');
            }
            dispatch(SET_WALLETBTN(false)); // Close the dialog after success
        } catch (error) {
            console.log('Transaction error:', error);
            if (error.message === "User rejected the request.") {
                toast.error("Transaction Rejected", { id: "token" })
                return
            }
            toast.error(error.message || 'Failed to launch token', { id: "token" });
        } finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        if (launchTokenSong) {
            setSongId(launchTokenSong?.song_id)
            setTokenName(launchTokenSong?.song_name?.length > 32 ? launchTokenSong?.song_name?.slice(0, 32) : launchTokenSong?.song_name)
            setWebsite(`https://soundofmeme.com/song/${launchTokenSong?.slug}`)
            setCoverPreview(launchTokenSong?.image_url)
        }
    }, [launchTokenSong, publicKey])

    useEffect(() => {
        if (launchTokenPlaylist) {
            setSongId(launchTokenPlaylist?.playlistId)
            setTokenName(launchTokenPlaylist?.name?.length > 32 ? launchTokenPlaylist?.name?.slice(0, 32) : launchTokenPlaylist?.name)
            setWebsite(`https://soundofmeme.com/playlist?id=${launchTokenPlaylist?.playlistSlug}`)
            setCoverPreview(launchTokenPlaylist?.playlistCoverImage || frog)
        }
    }, [launchTokenPlaylist, publicKey])

    const validateX = (value) => {
        const xRegex = /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/[a-zA-Z0-9_]{1,15}\/?$/;
        if (value && !xRegex.test(value)) {
            setXError('Invalid X URL format');
        } else {
            setXError('');
        }
    };

    const validateTelegram = (value) => {
        const telegramRegex = /^(https?:\/\/)?(www\.)?t\.me\/[a-zA-Z0-9_]{5,32}\/?$/;
        if (value && !telegramRegex.test(value)) {
            setTelegramError('Invalid Telegram URL format');
        } else {
            setTelegramError('');
        }
    };


    const onClose = () => {
        dispatch(SET_WALLETBTN(false));
        dispatch(SET_LAUNCHTOKENSONG(null));
        dispatch(SET_LAUNCHTOKENPLAYLIST(null));
        setBuyAmount(0.001)
        setTokenName('')
        setTicker('')
        setDescription('')
        setWebsite('')
        setTwitter('')
        setTelegram('')
        setCoverPreview(null)
        setCover(null)
        setXError('')
        setTelegramError('')
        setBuyAmountError('')
    }
    const handleUpgrade = () => {
        onClose()
        navigate('/premium')
    }

    return (
        <Dialog as='div' className="relative z-[999] focus:outline-none" open={walletBtn} onClose={onClose}>
            <div className="fixed inset-0 z-[999999] w-screen overflow-y-auto bg-slate-900/90 backdrop-blur-lg">
                <div className="fixed top-4 right-4 z-[9999999]">
                    <X className='h-8 w-8 p-1.5 text-slate-300 hover:text-green-400 cursor-pointer rounded-full bg-slate-800/80 hover:bg-slate-700/80 transition-all'
                        onClick={() => dispatch(SET_WALLETBTN(false))} />
                </div>
                <DialogPanel className="min-h-screen flex items-center justify-center sm:p-4 p-2" onClick={onClose}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-7xl bg-slate-800/90 backdrop-blur-xl rounded-xl border border-green-700/50 p-3 md:p-8 md:pt-6 transition-all transform">
                        <div className='flex flex-col lg:flex-row lg:items-center justify-between mb-6 md:mb-8 md:gap-4'>
                            <div className="">
                                <h2 className="text-2xl md:text-3xl font-bold text-green-400 flex items-center">
                                    <DollarSign className="h-6 w-6 md:h-8 md:w-8" />Launch Your {launchTokenSong ? "Song" : "Playlist"} Token
                                </h2>
                                <p className="text-slate-300 mt-2 max-w-prose">Create your own token for the {launchTokenSong ? "song" : "playlist"}. Connect your wallet to get started.</p>
                                {!userHavePlan && <p className="text-amber-300 mt-2 max-w-prose text-xl font-semibold flex items-center gap-2"> You need to have a active subscription plan to launch a token.</p>}
                            </div>
                            <div className="w-full md:w-auto mt-4 md:mt-0 flex items-center sm:gap-2 gap-1">
                                {!userHavePlan && <Button onClick={handleUpgrade} className='mt-2 h-12 my-1 bg-green-400 hover:bg-green-500 text-black font-semibold rounded-md sm:px-4 px-2 py-2 flex items-center gap-2 text-lg'> <Rocket size={20} /> Upgrade Plan</Button>}
                                <WalletMultiButton className='!bg-indigo-600 hover:!bg-indigo-700 !rounded-lg !text-sm md:!text-base !py-4 !w-full !md:w-auto !flex !items-center !justify-center' />
                            </div>
                        </div>

                        <div className="flex flex-col w-full">
                            <div className='flex lg:flex-row flex-col gap-6'>
                                <div className="w-full flex flex-col">
                                    <div className='flex-grow' >
                                        <div className={" group relative bg-slate-900/50 p-4 rounded-xl " + (connected ? ' hover:border-green-400 transition-all duration-300 border  border-dashed border-green-700' : ' cursor-not-allowed border  border-dashed border-slate-700')}>
                                            {coverPreview ? (
                                                <div className="relative w-full aspect-square rounded-lg overflow-hidden shadow-xl">
                                                    <img src={coverPreview} alt="Cover preview" className="w-full h-full object-cover transform " />
                                                    <Button disabled={loading || !connected} variant="ghost" className="absolute top-2 right-4 flex items-center gap-1 bg-green-400 hover:bg-green-500 text-black backdrop-blur-sm rounded" onClick={() => { setCoverPreview(null); setCover(null); }}>Change Cover</Button>
                                                </div>
                                            ) : (
                                                <div className={"w-full aspect-square flex flex-col items-center justify-center space-y-4" + (connected ? 'cursor-default' : 'cursor-not-allowed')}>
                                                    <div className="p-4 bg-slate-800/50 rounded-full"><Upload className="h-8 w-8 md:h-10 md:w-10 text-slate-500 " /></div>
                                                    <div className="text-center space-y-1">
                                                        <p className="text-sm md:text-base text-slate-300 font-medium">Drag & drop or <span className={(connected ? 'cursor-pointer text-green-400' : 'cursor-not-allowed text-green-600/50')} onClick={() => document.getElementById('cover').click()}>browse files</span></p>
                                                        <p className="text-xs md:text-sm text-slate-500">Recommended: (JPEG, PNG, WEBP)</p>
                                                    </div>
                                                    <Input id="cover" name="cover" type="file" accept="image/*" className="hidden" onChange={handleFileChange} disabled={!connected || !userHavePlan} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className='w-full space-y-2 lg:pt-4 hidden lg:block '>
                                        <div className="flex flex-col items-center gap-4">
                                            <Button disabled={!connected || loading || !userHavePlan} onClick={handleSubmit} className="w-full h-12 bg-gradient-to-r from-green-500/90 to-teal-400/90 hover:bg-green-400 text-slate-900 text-sm md:text-lg font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed">Launch Token</Button>
                                        </div>
                                    </div>
                                </div>
                                <div className='w-full space-y-3 lg:space-y-4'>
                                    <div className="space-y-1">
                                        <Label className="text-slate-300 text-sm md:text-base flex justify-between">
                                            <span>Token Name <span className="text-red-400">*</span></span>
                                            <span className="text-slate-400 text-sm">{tokenName.length}/32</span>
                                        </Label>
                                        <input 
                                            name="name" 
                                            placeholder="Enter token name" 
                                            maxLength={32}
                                            className={"h-11 md:h-12 text-sm bg-slate-900/50 text-slate-200 placeholder-slate-500 placeholder:text-sm rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400/80 block w-full p-2 disabled:opacity-50 disabled:cursor-not-allowed " + (connected ? 'border  border-green-700' : 'border-2 border-slate-700')} 
                                            value={tokenName} 
                                            onChange={(e) => setTokenName(e.target.value)} 
                                            disabled={!connected || loading || !userHavePlan} 
                                            required 
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-slate-300 text-sm md:text-base">Ticker <span className="text-red-400">*</span></Label>
                                        <input name="ticker" placeholder="e.g. SOL" className={"h-11 md:h-12 text-sm bg-slate-900/50 text-slate-200 placeholder-slate-500 placeholder:text-sm rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400/80 block w-full p-2 disabled:opacity-50 disabled:cursor-not-allowed " + (connected ? 'border  border-green-700' : 'border-2 border-slate-700')} value={ticker} onChange={(e) => setTicker(e.target.value)} disabled={!connected || loading || !userHavePlan} required />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-slate-300 text-sm md:text-base">Description</Label>
                                        <textarea name="description" disabled={!connected || loading || !userHavePlan} placeholder="Enter token description" className={"bg-slate-900/50 text-slate-200 min-h-24 md:min-h-28 w-full rounded-md p-2 placeholder-slate-500 placeholder:text-sm focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400/80 disabled:opacity-50 disabled:cursor-not-allowed " + (connected ? ' cursor-text border  border-green-700' : 'disabled cursor-not-allowed bg-slate-900/20 border-2 border-slate-700')} value={description} onChange={(e) => setDescription(e.target.value)} />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-slate-300 text-sm md:text-base flex items-center"><Globe className="h-4 w-4 mr-2" /> Website <span className="text-xs text-slate-500 ml-2">(Auto-filled)</span></Label>
                                        <Input name="website" placeholder="https://soundofmeme.com/" className={"h-11 md:h-12 text-sm bg-slate-900/50 text-slate-200 placeholder-slate-200 placeholder:text-sm rounded-md" + (connected ? 'border  border-green-700' : ' border border-slate-700')} value={website} onChange={(e) => setWebsite(e.target.value)} disabled={true} />
                                    </div>
                                    <div className="space-y-1 relative">
                                        <Label className="text-slate-300 text-sm md:text-base flex items-center gap-2">
                                            <TwitterXIcon size={16} className="" /> Twitter
                                        </Label>
                                        <input name="twitter" placeholder="https://x.com/username" className={"h-11 md:h-12 text-sm bg-slate-900/50 text-slate-200 placeholder-slate-500 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400/80 block w-full p-2 disabled:opacity-50 disabled:cursor-not-allowed " + (connected ? 'border border-green-700' : 'border-2 border-slate-700') + (xError ? ' border-red-500' : '')} value={twitter} onChange={(e) => { setTwitter(e.target.value); validateX(e.target.value); }} disabled={!connected || loading || !userHavePlan} />
                                        {xError && (
                                            <span className="absolute right-0 -bottom-5 text-xs text-red-400">
                                                {xError}
                                            </span>
                                        )}
                                    </div>
                                    <div className="space-y-1 relative">
                                        <Label className="text-slate-300 text-sm md:text-base flex items-center">
                                            <MessageCircle className="h-4 w-4 mr-2" /> Telegram
                                        </Label>
                                        <input name="telegram" placeholder="https://t.me/username" className={"h-11 md:h-12 text-sm bg-slate-900/50 text-slate-200 placeholder-slate-500 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400/80 block w-full p-2 disabled:opacity-50 disabled:cursor-not-allowed " + (connected ? 'border border-green-700' : 'border-2 border-slate-700') + (telegramError ? ' border-red-500' : '')} value={telegram} onChange={(e) => { setTelegram(e.target.value); validateTelegram(e.target.value); }} disabled={!connected || loading || !userHavePlan} />
                                        {telegramError && (
                                            <span className="absolute right-0 -bottom-5 text-xs text-red-400">
                                                {telegramError}
                                            </span>
                                        )}
                                    </div>
                                    <div className="space-y-1 w-full">
                                        <Label className="text-slate-300 text-sm md:text-base flex items-center">
                                            <DollarSign className="h-4 w-4 mr-2" /> Buy Amount (SOL)
                                        </Label>
                                        <div className="relative">
                                            <input
                                                name="buy_amount"
                                                className={"h-11 md:h-12 text-sm bg-slate-900/50 text-slate-200 rounded-md pl-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400/80 block w-full p-2 disabled:opacity-50 disabled:cursor-not-allowed " +
                                                    (buyAmountError ? ' border-red-500 ' : '') + (connected ? ' border border-green-700 ' : ' border-2 border-slate-700 ')}
                                                type="number"
                                                min="0.001"
                                                step="0.001"
                                                placeholder="0.001"
                                                value={buyAmount}
                                                onChange={(e) => {
                                                    if (e.target.value < 0.001) {
                                                        setBuyAmount(e.target.value);
                                                        setBuyAmountError('Minimum amount is 0.001 SOL');
                                                    } else {
                                                        setBuyAmountError('');
                                                        setBuyAmount(e.target.value);
                                                    }
                                                }}
                                                disabled={!connected || loading || !userHavePlan}
                                            />
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                                                SOL
                                            </span>
                                            {buyAmountError && (
                                                <span className="absolute right-0 -bottom-5 text-xs text-red-400">
                                                    {buyAmountError}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className='w-full space-y-2 lg:pt-4 lg:hidden '>
                                    <div className="flex flex-col items-center gap-4">
                                        <Button disabled={!connected || loading || !userHavePlan} onClick={handleSubmit} className="w-full h-12 bg-gradient-to-r from-green-500/90 to-teal-400/90 hover:bg-green-400 text-slate-900 text-sm md:text-lg font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed">Launch Token</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}

export default LaunchTokenDialog;